import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../components/Card";
import Modal from "../../components/Modal";
import Chip from "../../components/Dashboard/Chip";
import { createCode, listCodes, updateCode } from "../../api/referrals.admin.service";
import {
  askReason,
  CursorPager,
  errorCodeOf,
  errorDetailsOf,
  isOk,
  StatusBadge,
  useCursorList,
  useErrorText,
  useFormat,
} from "./shared";
import styles from "./Referrals.module.sass";

const PROGRAMS = ["FRIEND", "PARTNER", "PROMO"];
const STATUSES = ["ACTIVE", "PAUSED", "REVOKED"];

/**
 * Códigos (06 §2.3): listado con usos y alta de códigos vanity (socio, usuario
 * o PROMO) con canal, límites y overrides. Pausar, reactivar o revocar con motivo.
 */
const CodesTab = () => {
  const { t } = useTranslation();
  const fmt = useFormat();
  const errorText = useErrorText();
  const [filters, setFilters] = useState({ programKey: "", status: "", q: "" });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const list = useCursorList(listCodes, { ...filters, limit: 30 });

  const set = (key, value) => setFilters((f) => ({ ...f, [key]: f[key] === value ? "" : value }));

  const changeStatus = async (code, status) => {
    const reason = status === "ACTIVE" ? null : askReason(t);
    if (status !== "ACTIVE" && !reason) return;
    const resp = await updateCode(code.id, { status, reason: reason || undefined });
    if (!isOk(resp)) setError(errorCodeOf(resp));
    else {
      setError(null);
      list.reload();
    }
  };

  return (
    <>
      <Card
        className={styles.card}
        title={t("referrals.codes.title")}
        classTitle="title-blue"
        head={
          <button type="button" className="button button-small" onClick={() => setCreating(true)}>
            + {t("referrals.codes.create")}
          </button>
        }
      >
        <div className={styles.filters}>
          <input
            className={styles.search}
            placeholder={t("referrals.codes.search")}
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          />
          {PROGRAMS.map((p) => (
            <Chip key={p} on={filters.programKey === p} onClick={() => set("programKey", p)}>
              {t(`referrals.program.${p}`)}
            </Chip>
          ))}
          <span className={styles.sep} />
          {STATUSES.map((s) => (
            <Chip key={s} on={filters.status === s} onClick={() => set("status", s)}>
              {t(`referrals.status.${s}`)}
            </Chip>
          ))}
        </div>
        {error && <div className={styles.errorBox}>{errorText(error)}</div>}
        {list.error && <div className={styles.empty}>{errorText(list.error)}</div>}
        {!list.error && list.items.length === 0 && (
          <div className={styles.empty}>{list.loading ? t("referrals.common.loading") : t("referrals.common.noData")}</div>
        )}
        <div className={styles.table}>
          {list.items.map((c) => (
            <div key={c.id} className={styles.row}>
              <code className={styles.codeCell}>{c.code}</code>
              <div className={styles.rowMain}>
                <div className={styles.rowTitle}>
                  {t(`referrals.program.${c.programKey}`)} · {t(`referrals.codes.kind_${c.kind}`)}
                  {c.channel ? ` · ${c.channel}` : ""}
                </div>
                <div className={styles.muted}>
                  {t("referrals.codes.uses", {
                    used: fmt.num(c.redemptions),
                    max: c.limits?.maxRedemptions ? fmt.num(c.limits.maxRedemptions) : "∞",
                  })}
                  {c.limits?.validUntil ? ` · ${t("referrals.codes.until", { date: fmt.date(c.limits.validUntil) })}` : ""}
                  {c.statusReason ? ` · ${c.statusReason}` : ""}
                </div>
              </div>
              <div className={styles.rowSide}>
                <StatusBadge status={c.status} />
                <div className={styles.rowActions}>
                  {c.status === "ACTIVE" && (
                    <button type="button" className={styles.linkBtn} onClick={() => changeStatus(c, "PAUSED")}>
                      {t("referrals.codes.pause")}
                    </button>
                  )}
                  {c.status === "PAUSED" && (
                    <button type="button" className={styles.linkBtn} onClick={() => changeStatus(c, "ACTIVE")}>
                      {t("referrals.codes.activate")}
                    </button>
                  )}
                  {c.status !== "REVOKED" && (
                    <button type="button" className={styles.linkBtnDanger} onClick={() => changeStatus(c, "REVOKED")}>
                      {t("referrals.codes.revoke")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <CursorPager list={list} />
      </Card>

      <Modal visible={creating} onClose={() => setCreating(false)} outerClassName={styles.modalOuter}>
        {creating && (
          <CreateCodeForm
            onDone={() => {
              setCreating(false);
              list.reload();
            }}
          />
        )}
      </Modal>
    </>
  );
};

const CreateCodeForm = ({ onDone }) => {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [form, setForm] = useState({
    code: "",
    programKey: "PROMO",
    ownerUserId: "",
    partnerId: "",
    channel: "",
    maxRedemptions: "",
    validFrom: "",
    validUntil: "",
    welcomeDays: "",
    refereeDiscountEnabled: "",
    reason: "",
  });
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [saving, setSaving] = useState(false);
  const field = (key) => ({ value: form[key], onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })) });

  const submit = async (e) => {
    e.preventDefault();
    const limits = {};
    if (form.maxRedemptions) limits.maxRedemptions = Number(form.maxRedemptions);
    if (form.validFrom) limits.validFrom = new Date(form.validFrom).toISOString();
    if (form.validUntil) limits.validUntil = new Date(form.validUntil).toISOString();
    const overrides = {};
    if (form.welcomeDays !== "") overrides.welcomeDays = Number(form.welcomeDays);
    if (form.refereeDiscountEnabled !== "") overrides.refereeDiscountEnabled = form.refereeDiscountEnabled === "true";
    setSaving(true);
    const resp = await createCode({
      code: form.code,
      programKey: form.programKey,
      ownerUserId: form.programKey === "FRIEND" ? form.ownerUserId || undefined : undefined,
      partnerId: form.programKey === "PARTNER" ? form.partnerId || undefined : undefined,
      channel: form.channel || undefined,
      limits: Object.keys(limits).length ? limits : undefined,
      overrides: Object.keys(overrides).length ? overrides : undefined,
      reason: form.reason || undefined,
    });
    setSaving(false);
    if (isOk(resp)) onDone();
    else {
      setError(errorCodeOf(resp));
      setDetails(errorDetailsOf(resp));
    }
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <h3 className={styles.detailTitle}>{t("referrals.codes.create")}</h3>
      <label>
        {t("referrals.codes.code")}
        <input required {...field("code")} placeholder="LAURA_YT" />
      </label>
      <label>
        {t("referrals.codes.program")}
        <select {...field("programKey")}>
          {PROGRAMS.map((p) => (
            <option key={p} value={p}>
              {t(`referrals.program.${p}`)}
            </option>
          ))}
        </select>
      </label>
      {form.programKey === "FRIEND" && (
        <label>
          {t("referrals.codes.ownerUserId")}
          <input required {...field("ownerUserId")} />
        </label>
      )}
      {form.programKey === "PARTNER" && (
        <label>
          {t("referrals.codes.partnerId")}
          <input required {...field("partnerId")} />
        </label>
      )}
      <label>
        {t("referrals.codes.channel")}
        <input {...field("channel")} placeholder="youtube" />
      </label>
      <div className={styles.formRow}>
        <label>
          {t("referrals.codes.maxRedemptions")}
          <input type="number" min="1" {...field("maxRedemptions")} />
        </label>
        <label>
          {t("referrals.codes.validFrom")}
          <input type="date" {...field("validFrom")} />
        </label>
        <label>
          {t("referrals.codes.validUntil")}
          <input type="date" {...field("validUntil")} />
        </label>
      </div>
      <div className={styles.formRow}>
        <label>
          {t("referrals.codes.welcomeDays")}
          <input type="number" min="0" max="365" {...field("welcomeDays")} placeholder={t("referrals.codes.fromProgram")} />
        </label>
        <label>
          {t("referrals.codes.discount")}
          <select {...field("refereeDiscountEnabled")}>
            <option value="">{t("referrals.codes.fromProgram")}</option>
            <option value="true">{t("referrals.common.yes")}</option>
            <option value="false">{t("referrals.common.no")}</option>
          </select>
        </label>
      </div>
      <label>
        {t("referrals.common.reason")}
        <input {...field("reason")} />
      </label>
      {error && (
        <div className={styles.errorBox}>
          {errorText(error)}
          {details.map((d) => (
            <div key={d.path + d.message}>
              <code>{d.path}</code>: {d.message}
            </div>
          ))}
        </div>
      )}
      <button type="submit" className="button" disabled={saving}>
        {t("referrals.codes.createSubmit")}
      </button>
    </form>
  );
};

export default CodesTab;
